import type { MisconceptionDefinition } from '../types'

/**
 * TIJD — Klok-lezen sub-registry.
 *
 * Misconcepties rond het aflezen en interpreteren van tijdsaanduidingen:
 * digitale klok (12- en 24-uurs notatie), analoge klok (uur-/minutenwijzer,
 * 5-minuten-roundup, fijne minuten-streepjes), en de bidirectionele
 * conversie tussen analoog/digitaal en 12-/24-uurs.
 *
 * Families: TIJD-DIGITAAL, TIJD-ANALOOG, TIJD-ANALOOG-GEDETAILLEERD,
 * TIJD-12-24, TIJD-CONVERSIE.
 *
 * Onderdeel van de TIJD-barrel-splitsing (juni 2026).
 */
export const TIJD_KLOK_MISCONCEPTIONS: MisconceptionDefinition[] = [
  // ─── 24-uurs notatie ────────────────────────────────────────────────────
  {
    id: 'tijd.00-vs-12-verward',
    label: '00:00 verward met 12:00',
    module: 'Tijd',
    description:
      'Leerling leest 00:00 als 12:00 (of omgekeerd) bij 24-uurs digitale tijden, door verwarring met de 12-uurs notatie.',
    typicalFamilies: ['TIJD-DIGITAAL', 'TIJD-12-24'],
  },
  {
    id: 'tijd.middernacht-vs-01:00',
    label: 'Middernacht verward met 01:00',
    module: 'Tijd',
    description:
      'Leerling denkt dat 01:00 middernacht is, of dat middernacht 01:00 is — de overgang naar de nieuwe dag wordt verkeerd geplaatst.',
    typicalFamilies: ['TIJD-DIGITAAL', 'TIJD-12-24'],
  },
  {
    id: 'tijd.24:00-bestaat-niet',
    label: '24:00 als geldige tijd gelezen',
    module: 'Tijd',
    description:
      'Leerling schrijft of accepteert 24:00 als geldige notatie, terwijl 24:00 als 00:00 van de volgende dag wordt genoteerd.',
    typicalFamilies: ['TIJD-DIGITAAL', 'TIJD-12-24'],
  },
  {
    id: 'tijd.dagdeel-verward',
    label: 'Dagdeel (am/pm) verward',
    module: 'Tijd',
    description:
      'Leerling verwart ochtend/middag/avond bij het omrekenen tussen 12- en 24-uurs notatie (bijv. 19:00 als 7:00 ’s ochtends).',
    typicalFamilies: ['TIJD-DIGITAAL', 'TIJD-12-24'],
  },

  // ─── Kwart / half / voor / over (analoog ↔ digitaal) ────────────────────
  {
    id: 'tijd.kwart-voor-over-verward',
    label: '"Kwart voor" verward met "kwart over"',
    module: 'Tijd',
    description:
      'Leerling verwisselt "kwart voor" en "kwart over" (bijv. kwart voor 4 als 4:15 i.p.v. 3:45).',
    typicalFamilies: ['TIJD-DIGITAAL', 'TIJD-ANALOOG'],
  },
  {
    id: 'tijd.half-voor-over-blijft-op-uur',
    label: '"Half" op verkeerd uur geplaatst',
    module: 'Tijd',
    description:
      'Leerling leest "half drie" als 3:30 in plaats van 2:30 — de Nederlandse conventie (half = uur eerder) wordt niet toegepast.',
    typicalFamilies: ['TIJD-DIGITAAL', 'TIJD-ANALOOG'],
  },
  {
    id: 'tijd.minuten-voor-na-half-verward',
    label: 'Minuten voor/na half door elkaar',
    module: 'Tijd',
    description:
      'Leerling verwart "tien voor half" en "tien over half" of telt minuten in de verkeerde richting rond het hele en halve uur.',
    typicalFamilies: ['TIJD-DIGITAAL', 'TIJD-ANALOOG'],
  },
  {
    id: 'tijd.kwart-vs-tien-voor',
    label: '"Kwart voor" verward met "tien voor"',
    module: 'Tijd',
    description:
      'Leerling verwart kwart (15 min) en tien minuten bij benoemingen als "kwart voor" vs "tien voor".',
    typicalFamilies: ['TIJD-DIGITAAL', 'TIJD-ANALOOG'],
  },
  {
    id: 'tijd.half-verkeerd-uur',
    label: '"Half" gekoppeld aan verkeerd uur',
    module: 'Tijd',
    description:
      'Bij conversie of klok-lezen plaatst de leerling "half" bij het verkeerde hele uur (variant van half-voor-over-blijft-op-uur, bij 12↔24 omrekenen).',
    typicalFamilies: ['TIJD-CONVERSIE', 'TIJD-DIGITAAL'],
  },

  // ─── Analoge klok — wijzers & precieze minuten ──────────────────────────
  // (juni 2026: TIJD-ANALOOG-GEDETAILLEERD coverage)
  {
    id: 'tijd.wijzers-verwisseld',
    label: 'Uur- en minutenwijzer verwisseld',
    module: 'Tijd',
    description:
      'Leerling leest de minutenwijzer als uurwijzer (of omgekeerd) en geeft een tijd waarin uur en minuten zijn omgedraaid (bijv. 10:35 i.p.v. 7:50). Klassieke fout op analoge klokken, vooral als beide wijzers in dezelfde sector staan.',
    typicalFamilies: ['TIJD-ANALOOG', 'TIJD-ANALOOG-GEDETAILLEERD'],
  },
  {
    id: 'tijd.5-min-naar-1-min',
    label: '5-minuten afgerond i.p.v. precies',
    module: 'Tijd',
    description:
      'Leerling rondt de positie van de minutenwijzer af op het dichtstbijzijnde veelvoud van 5, terwijl de vraag om precieze (1-minuut) aflezing vraagt (bijv. 8:32 → 8:30 of 8:35).',
    typicalFamilies: ['TIJD-ANALOOG-GEDETAILLEERD'],
  },
  {
    id: 'tijd.kleine-streep-genegeerd',
    label: 'Kleine minuten-streepjes genegeerd',
    module: 'Tijd',
    description:
      'Leerling telt alleen de grote streepjes (per 5 minuten) en negeert de kleine streepjes ertussen, waardoor de aflezing 1–4 minuten afwijkt van de werkelijke stand.',
    typicalFamilies: ['TIJD-ANALOOG-GEDETAILLEERD'],
  },

  // ─── 12/24-uurs conversie (PR-T2, mei 2026) ─────────────────────────────
  {
    id: 'tijd.pm-als-am',
    label: 'PM gelezen als AM',
    module: 'Tijd',
    description:
      'Leerling negeert het pm-deel bij conversie naar 24-uurs notatie (bijv. 3 pm als 03:00 i.p.v. 15:00).',
    typicalFamilies: ['TIJD-CONVERSIE', 'TIJD-12-24'],
  },
  {
    id: 'tijd.minuten-genegeerd',
    label: 'Minuten genegeerd bij conversie',
    module: 'Tijd',
    description:
      'Leerling rondt naar het hele uur en negeert de minuten bij het omrekenen tussen tijdnotaties.',
    typicalFamilies: ['TIJD-CONVERSIE', 'TIJD-12-24'],
  },
  {
    id: 'tijd.12-uur-vergeten',
    label: '12 uur niet opgeteld bij 24-uurs conversie',
    module: 'Tijd',
    description:
      "Leerling rekent een middag/avond-tijd om naar 24-uurs notatie maar vergeet er 12 uur bij op te tellen (bijv. 3 uur 's middags → 3:00 i.p.v. 15:00).",
    typicalFamilies: ['TIJD-CONVERSIE', 'TIJD-12-24'],
  },
  {
    id: 'tijd.kwart-over-voor-verwisseld',
    label: '"Kwart over" en "kwart voor" verwisseld in conversie',
    module: 'Tijd',
    description:
      'Leerling verwisselt bij conversie naar digitale tijd "kwart over" en "kwart voor" (bijv. kwart voor 4 → 4:15 i.p.v. 3:45).',
    typicalFamilies: ['TIJD-CONVERSIE', 'TIJD-ANALOOG'],
  },
  {
    id: 'tijd.dubbele-fout-12-en-kwart',
    label: 'Dubbele fout: 12-uur én kwart-over/voor verwisseld',
    module: 'Tijd',
    description:
      'Leerling maakt zowel de 12-uur-conversiefout als de kwart-over/voor-verwisseling in dezelfde vraag.',
    typicalFamilies: ['TIJD-CONVERSIE'],
  },
  {
    id: 'tijd.12-uur-onnodig-bijtellen',
    label: '12 uur onnodig bijgeteld',
    module: 'Tijd',
    description:
      "Leerling telt 12 uur op bij een ochtend-tijd waar dat niet hoort (bijv. 7 uur 's ochtends → 19:00 i.p.v. 07:00).",
    typicalFamilies: ['TIJD-CONVERSIE', 'TIJD-12-24'],
  },
  {
    id: 'tijd.over-voor-verwisseld',
    label: '"Over" en "voor" verwisseld bij minuten-conversie',
    module: 'Tijd',
    description:
      'Leerling verwisselt "over" en "voor" bij het omrekenen van analoge minuten-aanduidingen naar digitale tijd.',
    typicalFamilies: ['TIJD-CONVERSIE', 'TIJD-ANALOOG'],
  },
  {
    id: 'tijd.uur-minuten-omgedraaid',
    label: 'Uur en minuten omgedraaid in notatie',
    module: 'Tijd',
    description:
      'Leerling draait uur en minuten om in de digitale notatie (bijv. 3:45 → 45:03).',
    typicalFamilies: ['TIJD-CONVERSIE', 'TIJD-DIGITAAL'],
  },
]
